<template>
  <div class="card">
    <div class="setting-group-billing-page">
      <div class="message" :class="`${messageGroupBilling.className}`" v-html="messageGroupBilling.content"></div>

      <!-- データグループ請求を確認 -->
      <template v-if="showFormGroupBilling">
        <div class="caption-info">
          <div class="caption">{{ captionGroupBilling }}</div>
          <div class="group-billing-note">※ご登録の内容が、請求先となります。</div>
        </div>

        <!-- グループ請求書のプレビュー -->
        <template v-if="isPreviewStep">
          <preview-group-billing :groupBilling="groupBilling" />
          <div class="confirm-checkbox">
            <el-checkbox label="上記のご契約内容で同意" v-model="isConfirmSubmit" />
          </div>

          <div class="footer-right">
            <el-button type="primary" :disabled="!isConfirmSubmit" @click.prevent="updateGroupBilling()">
              この内容で登録する
            </el-button>
            <el-button @click.prevent="prevStep()"> 前の画面へ戻る </el-button>
          </div>
        </template>

        <!-- グループ請求書を更新する -->
        <template v-else>
          <group-billing-form
            ref="groupBillingFormRef"
            :group-billing="groupBilling"
            @input="groupBilling = $event"
          ></group-billing-form>

          <div class="footer-right">
            <el-button type="primary" :disabled="isNextDisabled" @click.prevent="nextStep()"> 確認画面へ </el-button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import Vue, { defineComponent } from 'vue'

import GroupBillingForm from '@/client/components/_organisms/GroupBillingForm.vue'
import { COUNTRY_DEFAULT } from '@/client/constant'
import { EMAIL_FORMAT, TELEPHONE_FORMAT, ZIP_CODE_FORMAT } from '@/client/constant/regex'
import api from '@/client/core/api'
import PreviewGroupBilling from '@/client/features/setting_group/group_billing'
import { useSystemStore } from '@/client/store/useSystemStore'
export default defineComponent({
  components: {
    PreviewGroupBilling,
    GroupBillingForm
  },
  data() {
    return {
      isPreviewStep: false,
      isConfirmSubmit: false,
      isSubmit: false,
      groupBilling: {
        companyName: '',
        departName: '',
        personName: '',
        email: '',
        countryCode: COUNTRY_DEFAULT,
        zipCode: '',
        address: '',
        telephone: ''
      }
    }
  },
  computed: {
    // 配列形式だと、他の computed から参照したときに vue-tsc が型を解決できない
    ...mapState(useSystemStore, {
      currentGroup: state => state.currentGroup,
      groups: state => state.groups
    }),

    showFormGroupBilling(): boolean {
      return Boolean(!this.currentGroup?.contractConfirmAt && this.currentGroup?.isContractManager)
    },

    messageGroupBilling(): { className: string; content: string } {
      if (!this.currentGroup?.id)
        return {
          className: 'message-warning',
          content:
            'グループに所属していないため、データを表示できません。<br />ご利用される方は、右下のサポートチャットよりお問い合わせください。'
        }

      const messageInfo = {
        className: 'message-info',
        content: ''
      }

      if (this.showFormGroupBilling && this.isPreviewStep) {
        messageInfo.content =
          '登録内容をご確認の上、よろしければ[上記のご契約内容で同意]にチェックのうえ、[この内容で登録する]ボタンをクリックしてください。'
      } else
        messageInfo.content =
          'このグループは、ご契約情報の入力が完了していません。<br />代表者による入力が完了後、サービスをご利用いただけます。'

      return messageInfo
    },

    captionGroupBilling(): string {
      if (this.isPreviewStep) return '契約情報の確認'
      return '契約情報の入力'
    },

    isNextDisabled(): boolean {
      return (
        !this.groupBilling.companyName ||
        !this.groupBilling.personName ||
        !this.groupBilling.email ||
        !EMAIL_FORMAT.test(this.groupBilling.email) ||
        !this.groupBilling.countryCode ||
        !this.groupBilling.zipCode ||
        !ZIP_CODE_FORMAT.test(this.groupBilling.zipCode) ||
        !this.groupBilling.address ||
        !this.groupBilling.telephone ||
        !TELEPHONE_FORMAT.test(this.groupBilling.telephone)
      )
    }
  },
  async created() {
    if (this.currentGroup?.id) await this.fetchGroupBilling(this.currentGroup.id)
  },
  methods: {
    ...mapActions(useSystemStore, ['setCurrentGroup', 'setGroups']),
    nextStep() {
      ;(this.$refs.groupBillingFormRef as Vue & { validateForm: () => Promise<unknown> })
        .validateForm()
        .then(() => {
          this.isPreviewStep = true
        })
        .catch(() => {
          this.isPreviewStep = false
        })
    },

    prevStep() {
      this.isPreviewStep = false
      this.isConfirmSubmit = false
    },

    /**
     * 支払い情報を登録する
     */
    async updateGroupBilling() {
      // 登録ボタンはグループがあるときだけ表示される
      const currentGroup = this.currentGroup
      if (!currentGroup) return

      this.isSubmit = true

      // グループ請求データを保存
      const result = await api.put(`/v1/group_billings/${currentGroup.id}`, this.groupBilling)
      setTimeout(() => {
        this.isSubmit = false
      }, 2000)

      if (result.error || !result.data) {
        this.$notify({
          title: 'ご契約内容の登録に失敗しました',
          message: '恐れ入りますが、時間をおいて再度お試しください。',
          customClass: 'danger error-group-billing',
          duration: 5000
        })

        return
      }

      // 成功メッセージを表示
      this.$notify({
        title: 'ご契約内容の登録が完了しました',
        message: 'サービスをご利用いただけます。<br> まずはアカウントを登録しましょう。',
        customClass: 'success',
        dangerouslyUseHTMLString: true,
        duration: 5000
      })

      const newGroups = this.groups.map(group => {
        if (group.id === currentGroup.id) {
          return { ...group, contractConfirmAt: new Date().toISOString() }
        }
        return group
      })

      // グループ一覧を更新する
      this.setGroups(newGroups)

      // 現在のグループを更新する
      this.setCurrentGroup(currentGroup.id)

      return this.$router.push('/campaigns')
    },

    async fetchGroupBilling(groupId: number) {
      const result = await api.get(`/v1/group_billings/${groupId}`)
      // グループ管理者権限がない場合、キャンペーン画面に移動
      if (!result.data && result.error && result.error.type === 'PERMISSION_DENIED') {
        return
      }

      if (result.data) {
        // グループ請求先情報を設定する
        this.groupBilling = {
          companyName: result.data.companyName,
          departName: result.data.departName,
          personName: result.data.personName,
          email: result.data.email,
          address: result.data.address,
          countryCode: result.data.countryCode || COUNTRY_DEFAULT,
          zipCode: result.data.zipCode,
          telephone: result.data.telephone
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/client/assets/scss/variables.scss' as *;

.setting-group-billing-page {
  :deep(.csv-loading) {
    .el-loading-spinner {
      top: 40vh;

      .el-loading-text {
        white-space: pre-line;
      }
    }
  }
}

.message {
  padding: $spacing-unit 2 * $spacing-unit;
}

.caption-info {
  margin: 2 * $spacing-unit 0;
}

.group-billing-note {
  margin-top: $spacing-unit;
  font-size: 12px;
  font-weight: 400;
  line-height: 2 * $spacing-unit;
}

.footer-right {
  display: flex;
  flex-direction: row-reverse;
  gap: 2 * $spacing-unit;
  justify-content: end;

  .el-button {
    box-shadow: unset !important;
  }
}
</style>
<style lang="scss">
@use '@/client/assets/scss/variables.scss' as *;

.error-group-billing {
  width: 400px !important;
}

.confirm-checkbox {
  .el-checkbox {
    box-shadow: unset !important;
  }

  .el-checkbox__label {
    color: $text-color !important;
  }

  margin-top: 2 * $spacing-unit;
}
</style>
